import { FormBase } from "../../util/form-base-form";
import { Roles } from "../../constants/roles";

class PostEditForm extends FormBase {
    public getPreviewRoot(root: ParentNode): HTMLElement | null {
        return this.queryElement(root, `[data-role="${Roles.POST_PREVIEW_ROOT}"]`, HTMLElement);
    }

    public getOgpPreviewTab(root: ParentNode): HTMLElement | null {
        return this.queryElement(root, `[data-role="${Roles.OGP_PREVIEW_TAB}"]`, HTMLElement);
    }

    public getOgpPreviewTabWithin(root: ParentNode | null | undefined): HTMLElement | null {
        return this.getOgpPreviewTab(root || document);
    }

    public getContentTextarea(root: ParentNode): HTMLTextAreaElement | null {
        return this.queryElement(
            root,
            `[data-role="${Roles.POST_CONTENT}"], textarea[name="content"]`,
            HTMLTextAreaElement,
        );
    }

    public getContentTextareaWithin(root: ParentNode | null | undefined): HTMLTextAreaElement | null {
        return this.getContentTextarea(root || document);
    }

    public getWysiwygContent(root: ParentNode): HTMLElement | null {
        return this.queryElement(root, `[data-role="${Roles.WYSIWYG_CONTENT}"]`, HTMLElement);
    }

    public getSnsLinkInputs(root: ParentNode): HTMLInputElement[] {
        return this.queryElements(root, `[data-role="${Roles.SNS_LINK_INPUT}"]`, HTMLInputElement);
    }

    public getPostSnsLinksData(root: ParentNode): HTMLElement | null {
        return this.queryElement(root, `[data-role="${Roles.POST_SNS_LINKS_DATA}"]`, HTMLElement);
    }

    public getLinkedProductIdInput(root: ParentNode): HTMLInputElement | null {
        return this.queryElement(
            root,
            `[data-role="${Roles.POST_PRODUCT_LINKER_VIEW}"] [data-role="${Roles.POST_PRODUCT_ID}"]`,
            HTMLInputElement,
        );
    }

    public getEditForm(root: ParentNode): HTMLElement | null {
        return this.queryElement(root, `[data-role="${Roles.POST_EDIT_FORM}"]`, HTMLElement);
    }

    public getTitleInput(root: ParentNode): HTMLInputElement | null {
        return this.getInput(root, 'input[name="post_title"]');
    }

    public getSnsLinksList(root: ParentNode): HTMLElement | null {
        return this.queryElement(root, `[data-role="${Roles.SNS_LINKS_LIST}"]`, HTMLElement);
    }

    public getPostContent(root: ParentNode): HTMLElement | null {
        return this.queryElement(
            root,
            `[data-role="${Roles.POST_DETAIL_CONTENT}"], [data-role="${Roles.POST_CONTENT_DISPLAY}"]`,
            HTMLElement,
        );
    }

    public getPostHeader(root: ParentNode): HTMLElement | null {
        return this.queryElement(root, `[data-role="${Roles.POST_DETAIL_HEADER}"]`, HTMLElement);
    }

    public getAttachmentsSection(root: ParentNode): HTMLElement | null {
        return this.queryElement(root, `[data-role="${Roles.POST_ATTACHMENTS_SECTION}"]`, HTMLElement);
    }

    public getMediaSection(root: ParentNode): HTMLElement | null {
        return this.queryElement(root, ".post-detail-media-section", HTMLElement);
    }

    public getUserProfileSection(root: ParentNode): HTMLElement | null {
        return this.queryElement(root, ".post-detail-user-profile", HTMLElement);
    }

    public getCommentsSection(root: ParentNode): HTMLElement | null {
        return this.queryElement(root, `[data-role="${Roles.COMMENTS_SECTION}"]`, HTMLElement);
    }

    public getClosestForm(root: Element | null | undefined): HTMLFormElement | null {
        return this.closest(root, "form", HTMLFormElement);
    }

    public getHiddenSnsLinkInputs(root: ParentNode): HTMLInputElement[] {
        return this.queryElements(root, 'input[type="hidden"][name="sns_links[]"]', HTMLInputElement);
    }

    public getPostDetailRoot(root: Element | null | undefined): HTMLElement | null {
        return this.closest(root, `[data-role="${Roles.POST_DETAIL}"]`, HTMLElement);
    }

    public getFormContainer(postKey: string): HTMLElement | null {
        return this.queryElement(
            document,
            `[data-role="${Roles.POST_EDIT_FORM}"][data-post-id="${postKey}"]`,
            HTMLElement,
        );
    }

    public getFormInner(root: ParentNode): HTMLElement | null {
        return this.queryElement(root, `[data-role="${Roles.POST_EDIT_FORM_INNER}"], form`, HTMLElement);
    }

    public getSnsAddBtn(root: ParentNode): HTMLButtonElement | null {
        return this.getButton(root, `[data-role="${Roles.SNS_OPEN}"]`);
    }

    public getYoutubeSection(root: ParentNode): HTMLElement | null {
        return this.queryElement(root, `[data-role="${Roles.YOUTUBE_LINKS_CONTAINER}"]`, HTMLElement);
    }

    public getYoutubeContainer(root: ParentNode): HTMLElement | null {
        return this.queryElement(root, `[data-role="${Roles.YOUTUBE_LINKS_LIST}"]`, HTMLElement);
    }

    public getSnsContainer(root: ParentNode): HTMLElement | null {
        return this.queryElement(root, `[data-role="${Roles.SNS_LIVE_PREVIEW}"]`, HTMLElement);
    }

    public getOgpContainer(root: ParentNode): HTMLElement | null {
        return this.queryElement(root, `[data-role="${Roles.OGP_LIVE_PREVIEW}"]`, HTMLElement);
    }

    public getPreviewRichText(root: ParentNode): HTMLElement | null {
        return this.queryElement(
            root,
            `[data-role="${Roles.POST_PREVIEW_ROOT}"] [data-role="${Roles.PREVIEW_RICH_HOST}"]`,
            HTMLElement,
        );
    }

    public getWysiwygEditor(root: ParentNode): HTMLElement | null {
        return this.queryElement(root, `[data-role="${Roles.WYSIWYG_EDITOR}"]`, HTMLElement);
    }

    public getExistingImageElements(root: ParentNode): HTMLImageElement[] {
        return this.queryElements(root, `[data-item-role="${Roles.EXISTING_IMAGE_TILE}"] img`, HTMLImageElement);
    }

    public getExistingVideoSources(root: ParentNode): HTMLSourceElement[] {
        return this.queryElements(
            root,
            `[data-item-role="${Roles.EXISTING_VIDEO_TILE}"] video source`,
            HTMLSourceElement,
        );
    }

    public getImageManager(root: ParentNode, postKey: string): HTMLElement | null {
        return this.queryElement(root, `[data-role="${Roles.IMAGE_MANAGER}"][data-post-id="${postKey}"]`, HTMLElement);
    }

    public getVideoManager(root: ParentNode, postKey: string): HTMLElement | null {
        return this.queryElement(root, `[data-role="${Roles.VIDEO_MANAGER}"][data-post-id="${postKey}"]`, HTMLElement);
    }

    public getTitleElement(root: ParentNode): HTMLElement | null {
        return this.queryElement(
            root,
            `[data-role="${Roles.POST_DETAIL_TITLE}"], [data-role="${Roles.POST_CARD_TITLE}"]`,
            HTMLElement,
        );
    }
}

const postEditForm = new PostEditForm();

export { PostEditForm, postEditForm };
